import React, { useRef } from "react";
import { Modal, Typography, Button, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { ResponsiveLine } from "@nivo/line";
import { saveAs } from "file-saver";

const { Text } = Typography;

// Minimum year shown in timeplots, consistent with the external query page year list
const MIN_TIME_SERIES_YEAR = 1989;

export default function TimeSeriesModal({ visible, onClose, countryName, indicatorName, unit, data }) {
  const chartRef = useRef(null);

  const chartData = data?.length
    ? [
        {
          id: indicatorName,
          data: data
            .filter((p) => p.value !== null && p.year >= MIN_TIME_SERIES_YEAR)
            .map((p) => ({ x: p.year, y: p.value })),
        },
      ]
    : [];

  const handleExport = async () => {
    const svgEl = chartRef.current?.querySelector("svg");
    if (!svgEl) return;
    const svgClone = svgEl.cloneNode(true);
    svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const styleEl = document.createElement("style");
    styleEl.textContent = `
      text { font-family: -apple-system, BlinkMacSystemFont, sans-serif; }
      .nivo-line-tooltip { display: none; }
      .data-label { font-family: -apple-system, BlinkMacSystemFont, sans-serif; }
    `;
    svgClone.insertBefore(styleEl, svgClone.firstChild);

    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgClone);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.scale(2, 2);
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      ctx.font = "bold 11px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.fillStyle = "#333";
      ctx.textAlign = "center";
      ctx.fillText(`${indicatorName} - ${countryName}`, img.width / 2, 18);

      const dp = chartData[0]?.data || [];
      if (dp.length > 1) {
        const plotLeft = 70;
        const plotTop = 30;
        const plotRight = img.width - 40;
        const plotBottom = img.height - 60;
        const plotW = plotRight - plotLeft;
        const plotH = plotBottom - plotTop;
        const years = dp.map((p) => p.x);
        const vals = dp.map((p) => p.y);
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);
        const maxVal = Math.max(...vals);
        const yearRange = maxYear - minYear || 1;
        const valRange = maxVal || 1;

        ctx.font = "bold 7px -apple-system, BlinkMacSystemFont, sans-serif";
        ctx.fillStyle = "#1565C0";
        ctx.textAlign = "center";
        const minDist = 45; // minimum pixel distance between labels to avoid overlap
        let lastPx = -Infinity;
        dp.forEach((p) => {
          const px = plotLeft + ((p.x - minYear) / yearRange) * plotW;
          const py = plotBottom - (p.y / valRange) * plotH;
          if (px - lastPx < minDist) return; // skip if too close to previous label
          lastPx = px;
          ctx.fillText(typeof p.y === "number" ? Number(p.y).toLocaleString() : String(p.y), px, py - 8);
        });
      }

      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (blob) saveAs(blob, `${indicatorName.replace(/[^a-z0-9]/gi, "_")}.png`);
      });
    };
    img.src = url;
  };

  return (
    <Modal
      title={
        <span>
          {indicatorName} — <Text type="secondary">{countryName}</Text>
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={760}
      destroyOnClose
    >
      <div style={{ marginBottom: 12, textAlign: "right" }}>
        {chartData.length > 0 && chartData[0].data.length > 0 && (
          <Button icon={<DownloadOutlined />} size="small" onClick={handleExport}>
            Export PNG
          </Button>
        )}
      </div>
      <div ref={chartRef} style={{ height: 400 }}>
        {chartData.length > 0 && chartData[0].data.length > 0 ? (
          <ResponsiveLine
            data={chartData}
            margin={{ top: 30, right: 40, bottom: 60, left: 70 }}
            xScale={{ type: "linear", min: "auto", max: "auto" }}
            yScale={{ type: "linear", min: 0, max: "auto" }}
            axisBottom={{
              legend: "Year",
              legendOffset: 40,
              legendPosition: "middle",
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
            }}
            axisLeft={{
              legend: unit || "Value",
              legendOffset: -60,
              legendPosition: "middle",
              tickSize: 5,
              tickPadding: 5,
            }}
            pointSize={8}
            pointColor={{ from: "color", modifiers: [] }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            enableArea
            areaOpacity={0.15}
            enableCrosshair
            useMesh
            colors={["#1565C0"]}
            lineWidth={3}
            tooltip={({ point }) => (
              <div
                style={{
                  background: "#fff",
                  padding: "6px 12px",
                  borderRadius: 6,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  fontSize: 13,
                  border: "1px solid #e8e8e8",
                }}
              >
                <strong>{point.serieId}</strong>
                <br />
                Year: <strong>{point.data.x}</strong>
                <br />
                Value: <strong>{Number(point.data.y).toLocaleString()}</strong>
              </div>
            )}
          />
        ) : (
          <div
            style={{
              textAlign: "center",
              paddingTop: 160,
              color: "#999",
              fontSize: 14,
            }}
          >
            No time-series data available.
          </div>
        )}
      </div>
    </Modal>
  );
}
